import type {AppThunk} from '../../../../actions/types'
import type {PipelineId} from '../../../../types'
import {getValidationErrorMessage} from '../utils/validators'
import type {ValidatorAdditionalProps, PipelineInfo} from '../utils/validators.types'

import {getAllFormsToValidate, validatorSelectors} from './EditPipelinePage.selectors'
import type {DeleteJobPayload} from './slices/EditPipelinePage.job.slice'
import {pipelineDraft, pipelineErrors} from './slices/EditPipelinePage.slices'
import type {PipelineFormParams} from './slices/EditPipelinePage.slices.types'

export const validateField =
  (
    formParams: PipelineFormParams,
    fieldName: string,
    pipelineNames?: PipelineInfo[],
  ): AppThunk<boolean> =>
  (dispatch, getState) => {
    const {pipelineId, jobId, type, id} = formParams
    dispatch(
      pipelineErrors.actions.clear({pipelineId, jobId, formType: type, formId: id, fieldName}),
    )
    const state = getState()
    const additionalProps: ValidatorAdditionalProps = {pipelineNames: pipelineNames ?? []}
    const validators = validatorSelectors[formParams.type]?.[fieldName]?.(
      state,
      formParams,
      additionalProps,
    )
    const message = validators != null ? getValidationErrorMessage(validators) : ''
    if (message) {
      dispatch(
        pipelineErrors.actions.add({
          pipelineId,
          jobId,
          formType: type,
          formId: id,
          fieldName,
          message,
        }),
      )
      return false
    }

    return true
  }

export const validateForm =
  (formParams: PipelineFormParams, pipelineNames?: PipelineInfo[]): AppThunk<boolean> =>
  dispatch => {
    const additionalProps: ValidatorAdditionalProps = {pipelineNames}
    const results = Object.keys(validatorSelectors[formParams.type] ?? {}).map(fieldName =>
      dispatch(validateField(formParams, fieldName, additionalProps.pipelineNames)),
    )
    return results.every(Boolean)
  }

export const validateAllForms =
  (
    pipelineId: PipelineId,
    filter: (form: PipelineFormParams) => boolean = () => true,
  ): AppThunk<boolean> =>
  (dispatch, getState) => {
    const results = getAllFormsToValidate(getState(), pipelineId)
      .filter(filter)
      .map(formParams => dispatch(validateForm(formParams)))
    return results.every(Boolean)
  }

export const deleteJob =
  (payload: DeleteJobPayload): AppThunk =>
  dispatch => {
    dispatch(pipelineDraft.actions.deleteJob(payload))
    const {id, jobId} = payload
    dispatch(pipelineErrors.actions.clear({pipelineId: id, jobId}))
  }
